// Logica di "Profili LED vuoti" (codice, prezzi a metro, clips, riepilogo).
// Copiata senza modifiche da LooseProfilesConfigurator.js (versione prima del redesign 2026-10).

export const PROFILE_LENGTHS = [100, 200, 300];
export const COVER_LENGTHS = [100, 200, 300, 600];

export const roundCurrency = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
export const formatCurrency = (value) => roundCurrency(value).toFixed(2);

export const formatEuroPerMeter = (value) => {
  if (value == null || Number.isNaN(value)) {
    return null;
  }

  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded)
    ? `${rounded.toFixed(0)}€/m`
    : `${rounded.toFixed(1).replace('.', ',')}€/m`;
};

export const getProfileMaterialPricePerMeter = (configData, modelName, finish) => {
  const finishOverride = configData.modelFinishPricesPerMeter?.[modelName]?.[finish];
  if (finishOverride != null) {
    return Number(finishOverride);
  }

  const modelPricePerMeter = configData.modelPricesPerMeter?.[modelName];
  const finishingPrice = configData.finishingPricePerMeter?.[finish];
  if (modelPricePerMeter == null || finishingPrice == null) {
    return null;
  }

  return modelPricePerMeter + finishingPrice;
};

export const getClipsTotal = (configData, modelName, quantity, lengthCm = null) => {
  const unit = Number(configData.clipsPrices?.[modelName] || 0);
  if (configData.clipsPriceMode?.[modelName] === 'fixedPair') {
    return roundCurrency(unit * 2 * quantity);
  }

  if (unit === 0.4) {
    return roundCurrency(unit * 5 * quantity);
  }

  const multiplier = lengthCm != null ? lengthCm / 100 : 5;
  return roundCurrency(unit * multiplier * quantity);
};

export const getProfileStartingNetPricePerMeter = (configData, model, discount) => {
  const finishes = model.availableFinishes || [];

  if (finishes.length === 0) {
    return null;
  }

  const grossPrices = finishes
    .map((finish) => getProfileMaterialPricePerMeter(configData, model.name, finish))
    .filter((price) => price != null)
    .map((price) => price * 4.5)
    .filter((price) => price != null);

  if (grossPrices.length === 0) {
    return null;
  }

  const minGrossPrice = Math.min(...grossPrices);
  return minGrossPrice * (1 - (discount || 0) / 100);
};

export const generateLooseCode = (configData, model, profileColor, selectedLength, cover) => {
  // Modelli senza numero nel nome (es. BATTISCOPA): il codice parte dal nome stesso.
  // modelCodes: prefisso esplicito (es. modelli senza cifre nel nome), come nel configuratore su misura.
  const modelCode =
    configData.modelCodes?.[model] || model.replace(/\D/g, '') || model.replace(/\s+/g, '').toUpperCase();
  const colorCode = configData.profileColorCodes?.[profileColor] || '';
  const lengthCode = String(selectedLength / 5).padStart(2, '0');
  let coverCode = cover ? cover.substring(0, 2).toUpperCase() : '';

  if (coverCode === 'SA') {
    coverCode = '8';
  }

  return `${modelCode}${lengthCode}${coverCode}${colorCode}`;
};

// Riepilogo prezzi (stesso calcolo di getPricingSnapshot)
export const getPricingSnapshot = (configData, {
  selectedModel, selectedColor, length, hasCover, selectedCoverType, coverLength,
  profileQuantity, coverQuantity, tappiQuantity, clipsQuantity, discount,
}) => {
  if (!selectedModel || !selectedColor || !length || (hasCover && (!selectedCoverType || !coverLength))) {
    return null;
  }

  const modelName = selectedModel.name;
  const profilePricePerMeter = getProfileMaterialPricePerMeter(configData, modelName, selectedColor);
  const coverPricePerMeter = hasCover ? configData.coverPrices?.[selectedCoverType] : 0;

  if (
    profilePricePerMeter == null ||
    coverPricePerMeter == null
  ) {
    return null;
  }

  const profileTotal = Math.round(profilePricePerMeter * (length / 100) * 4.5 * profileQuantity);
  const profileNetPrice = roundCurrency(profileTotal * (1 - (discount || 0) / 100));

  const coverTotal = hasCover
    ? Math.round(coverPricePerMeter * (coverLength / 100) * 4.5 * coverQuantity)
    : 0;
  const coverNetPrice = roundCurrency(coverTotal * (1 - (discount || 0) / 100));

  const tappiUnit = configData.tappiPrices?.[modelName] || 0;
  const tappiTotal = roundCurrency(tappiUnit * tappiQuantity * 5);
  const tappiNetPrice = roundCurrency(tappiTotal * (1 - (discount || 0) / 100));

  const clipsTotal = getClipsTotal(configData, modelName, clipsQuantity, length);
  const clipsNetPrice = roundCurrency(clipsTotal * (1 - (discount || 0) / 100));

  return {
    productCode: generateLooseCode(configData, modelName, selectedColor, length, hasCover ? selectedCoverType : ''),
    profileTotal,
    profileNetPrice,
    coverTotal,
    coverNetPrice,
    tappiTotal,
    tappiNetPrice,
    clipsTotal,
    clipsNetPrice,
    grandTotal: roundCurrency(profileTotal + coverTotal + tappiTotal + clipsTotal),
    grandNetTotal: roundCurrency(
      profileNetPrice + coverNetPrice + tappiNetPrice + clipsNetPrice
    ),
  };
};
